import { useCallback, useEffect, useRef, useState } from 'react'

const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '])

interface UseFollowLiveArgs {
  isLive: boolean
  contentVersion: number
  changedIds: string[]
  reduceMotion: boolean | null
}

/** Reader-respecting live follow: auto-scroll to changes only while "following".
 *  Any user scroll intent (wheel / touch / scroll keys — never our own programmatic
 *  scroll, which fires none of those events) pauses following; a change that arrives
 *  while paused raises hasNewUpdate for a catch-up affordance instead of yanking. */
export function useFollowLive({ isLive, contentVersion, changedIds, reduceMotion }: UseFollowLiveArgs) {
  const [following, setFollowing] = useState(true)
  const [hasNewUpdate, setHasNewUpdate] = useState(false)
  const lastChangedRef = useRef<string | null>(null)
  const handledVersionRef = useRef<number | null>(null)

  const scrollToSection = useCallback(
    (id: string) => {
      const el = document.querySelector(`[data-section-id="${CSS.escape(id)}"]`)
      el?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' })
    },
    [reduceMotion],
  )

  useEffect(() => {
    if (!isLive || changedIds.length === 0) return
    // one reaction per content version — a pause/resume flip must not replay the last change
    if (handledVersionRef.current === contentVersion) return
    handledVersionRef.current = contentVersion
    lastChangedRef.current = changedIds[0]
    if (following) {
      scrollToSection(changedIds[0])
    } else {
      setHasNewUpdate(true)
    }
  }, [contentVersion, isLive, changedIds, following, scrollToSection])

  useEffect(() => {
    if (!isLive || !following) return
    const pauseOnUserScroll = () => setFollowing(false)
    const pauseOnScrollKey = (e: KeyboardEvent) => {
      if (SCROLL_KEYS.has(e.key)) setFollowing(false)
    }
    window.addEventListener('wheel', pauseOnUserScroll, { passive: true })
    window.addEventListener('touchmove', pauseOnUserScroll, { passive: true })
    window.addEventListener('keydown', pauseOnScrollKey)
    return () => {
      window.removeEventListener('wheel', pauseOnUserScroll)
      window.removeEventListener('touchmove', pauseOnUserScroll)
      window.removeEventListener('keydown', pauseOnScrollKey)
    }
  }, [isLive, following])

  useEffect(() => {
    if (!isLive) setHasNewUpdate(false)
  }, [isLive])

  const resume = useCallback(() => {
    setFollowing(true)
    setHasNewUpdate(false)
    if (lastChangedRef.current) scrollToSection(lastChangedRef.current)
  }, [scrollToSection])

  const pause = useCallback(() => setFollowing(false), [])

  return { following, hasNewUpdate, resume, pause }
}
